import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config'; // Ensure this path is correct
import { MapPin } from 'lucide-react';
import styles from './PostcodeAutocomplete.module.css';

const PostcodeAutocomplete = ({ value, onSuggestionSelect, disabled, placeholder, onChange }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    // Sync state if parent updates the value prop (e.g. initial load or reset)
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleChange = async (e) => {
        const val = e.target.value;
        setInputValue(val);
        if (onChange) onChange(val);
        
        // Allow user to clear input
        if (val.length === 0 && onSuggestionSelect) {
             onSuggestionSelect({ fullAddress: '', postcode: '', suburb: '', state: '' });
        }

        if (val.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            // FIX FOR NSW ISSUE: 
            // If your API supports it, ensure no 'state' filter is applied here.
            // If the API is hardcoded to NSW, you cannot fix it from React.
            const response = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(val)}`);
            
            if (response.data && response.data.length > 0) {
                setSuggestions(response.data);
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error("Location search error:", error);
        }
    };

    const handleSelect = (item) => {
        // Create a nice display string
        const displayString = item.displayName || `${item.suburb}, ${item.state} ${item.postcode}`;
        
        setInputValue(displayString);
        setShowSuggestions(false);

        if (onSuggestionSelect) {
            onSuggestionSelect({
                fullAddress: displayString,
                postcode: item.postcode,
                suburb: item.suburb || item.locality,
                state: item.state || item.mystate, 
                // Ensure your API returns 'state' or 'mystate' correctly
            });
        }
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.inputContainer}>
                <MapPin className={styles.inputIcon} size={16} />
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleChange}
                    disabled={disabled}
                    placeholder={placeholder || "Start typing suburb..."}
                    className={styles.input}
                    autoComplete="off"
                />
            </div>
            
            {showSuggestions && suggestions.length > 0 && (
                <ul className={styles.suggestionsList}>
                    {suggestions.map((item, index) => (
                        <li key={index} onClick={() => handleSelect(item)} className={styles.suggestionItem}>
                            <MapPin size={14} style={{marginRight: '8px', color: '#64748B'}}/>
                            <span>
                                {/* Adjust these fields based on exactly what your API returns */}
                                {item.displayName || `${item.suburb}, ${item.state} ${item.postcode}`}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PostcodeAutocomplete;
